import React, { useState, useMemo } from 'react';
import {
  Tabs, Tab, Box, Paper, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel,
  Switch, Typography, Stack, Fade, Grow, Slide, Zoom,
  ListSubheader,
} from '@mui/material';
import {
  Ticket, Search, Luggage, ArrowUpCircle, Plus, Trash2,
  PlaneTakeoff, Copy, CheckCircle, Plane, Sparkles, Send,
} from 'lucide-react';
import { pnrApi } from '../services/pnrApi';
import { AirlineLoader, AirlineLoaderMini } from '../components/AirlineLoader';
import { appInsights } from '@/telemetry';
import './PnrGenerator.css';

/* ── Shared MUI sx tokens ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
    '&:hover': { backgroundColor: '#f8fafc' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 0 0 3px rgba(0,151,169,0.12)',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    '&.Mui-focused': { color: '#0097A9' },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
    transition: 'border-color 0.2s ease',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#94a3b8',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0097A9',
    borderWidth: '2px',
  },
};

const selectSx = {
  ...inputSx,
  '& .MuiSelect-select': { fontSize: '0.875rem' },
};

/* Error styling for validation – adds red border + subtle red glow */
const inputErrorSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444 !important' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444 !important', boxShadow: '0 0 0 3px rgba(239,68,68,0.10)' },
  '& .MuiInputLabel-root': { color: '#ef4444 !important' },
};

const btnPrimary = {
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  px: 3,
  py: 1.2,
  boxShadow: 'none',
  transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
  '&:hover': { boxShadow: '0 4px 14px rgba(1,66,106,0.25)', transform: 'translateY(-1px)' },
  '&:active': { transform: 'translateY(0)' },
};

const btnOutlined = {
  ...btnPrimary,
  borderColor: '#e2e8f0',
  color: '#475569',
  '&:hover': { borderColor: '#0097A9', color: '#0097A9', backgroundColor: 'rgba(0,151,169,0.04)' },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '16px',
    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
};

/* ── Constants ── */
const BUSINESS = ['J', 'C', 'D', 'I', 'U', 'E'];
const PREMIUM = ['W', 'P', 'R', 'Z'];
const MAIN = ['Y', 'B', 'H', 'K', 'M', 'L', 'V', 'S', 'N', 'Q', 'O', 'G', 'X', 'T'];
const SSR_CODES = ['DEAF', 'BLND', 'WCHR', 'MAAS', 'AVIH', 'PETC', 'UMNR'];
const MVP_CODES = ['No_Tier_Status', 'MVP', 'MVP_Gold', 'MVP_Gold_75', 'AA_RUBY', 'AA_EMLD', 'AA_SPHR', 'MVP_100K'];
const UPGRADE_AVAIL = { 'Upgrade Availability': ['U'], 'Award Availability': ['J', 'E', 'Y', 'T'] };

/* ── Result Table ── */
function ResultTable({ columns, keys, rows, title }) {
  if (!rows?.length) return null;
  const renderCell = (val) => {
    if (val == null) return '—';
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    return String(val);
  };
  return (
    <Fade in timeout={400}>
      <Box sx={{ mt: 3 }}>
        {title && <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</Typography>}
        <TableContainer sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {columns.map(c => <TableCell key={c} sx={{ fontWeight: 600, color: '#334155', fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid #e2e8f0' }}>{c}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' }, transition: 'background 0.15s ease', '&:last-child td': { borderBottom: 0 } }}>
                  {keys.map((k, j) => <TableCell key={j} sx={{ fontSize: '0.8rem', color: '#475569', py: 1.2 }}>{renderCell(row[k])}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Fade>
  );
}

/* ── Copy Button ── */
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 2000); };
  return (
    <IconButton size="small" onClick={copy} sx={{ transition: 'all 0.2s ease', '&:hover': { bgcolor: 'rgba(0,151,169,0.08)' } }}>
      {ok ? <CheckCircle size={16} color="#00AF66" /> : <Copy size={16} color="#64748b" />}
    </IconButton>
  );
}

/* ── Section Card ── */
function SectionCard({ title, icon: Icon, children, action }) {
  return (
    <Box sx={{
      border: '1px solid #e2e8f0', borderRadius: '14px', p: 2.5, mb: 2.5,
      transition: 'all 0.2s ease', position: 'relative', bgcolor: '#fff',
      '&:hover': { borderColor: '#cbd5e1' },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {Icon && <Icon size={16} color="#0097A9" />}
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

/* ══════════════════════════════════════════════════
   GENERATE PNR TAB
   ══════════════════════════════════════════════════ */
function GeneratePnrTab() {
  const [paxCount, setPaxCount] = useState('');
  const [ticket, setTicket] = useState(true);
  const [checkin, setCheckin] = useState(false);
  const [aaaCity, setAaaCity] = useState('SEA');
  const [segments, setSegments] = useState([]);
  const [ssrList, setSsrList] = useState([]);
  const [mvpList, setMvpList] = useState([]);
  const [bagList, setBagList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [segDlg, setSegDlg] = useState(false);
  const [segForm, setSegForm] = useState({ DepartureAirport: '', ArrivalAirport: '', FlightNumber: '', DepartureDate: '', Cabin: '' });
  const [segTouched, setSegTouched] = useState({});
  const [flightResults, setFlightResults] = useState([]);
  const [findingFlights, setFindingFlights] = useState(false);

  const [ssrDlg, setSsrDlg] = useState(false);
  const [ssrForm, setSsrForm] = useState({ SSR_Code: '', SSR_Pax_num: '' });
  const [mvpDlg, setMvpDlg] = useState(false);
  const [mvpForm, setMvpForm] = useState({ MP_CODE: '', MP_Pax_num: '' });
  const [bagDlg, setBagDlg] = useState(false);
  const [bagForm, setBagForm] = useState({ Bags_Pax_Num: '', Bag_Count: '' });

  /* AI Natural Language state */
  const [nlText, setNlText] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [nlError, setNlError] = useState('');
  const [nlSuccess, setNlSuccess] = useState('');

  const paxArr = useMemo(() => paxCount ? Array.from({ length: paxCount }, (_, i) => i + 1) : [], [paxCount]);

  /* Segment-dialog required checks */
  const segReqMet = segForm.DepartureAirport && segForm.ArrivalAirport && segForm.DepartureDate && segForm.Cabin;

  const touchSeg = (field) => setSegTouched(p => ({ ...p, [field]: true }));
  const segErr = (field) => segTouched[field] && !segForm[field];

  const openSegDlg = () => {
    setSegForm({ DepartureAirport: '', ArrivalAirport: '', FlightNumber: '', DepartureDate: '', Cabin: '' });
    setSegTouched({});
    setFlightResults([]);
    setSegDlg(true);
  };

  const findFlights = async () => {
    setSegTouched({ DepartureAirport: true, ArrivalAirport: true, DepartureDate: true, Cabin: true });
    if (!segReqMet) return;
    setFindingFlights(true);
    try {
      const d = await pnrApi.findFlights({
        departureAirportCode: segForm.DepartureAirport,
        arrivalAirportCode: segForm.ArrivalAirport,
        departureDateTimeStart: segForm.DepartureDate,
        classOfService: segForm.Cabin,
        numberOfPassengers: Number(paxCount) || 1,
      });
      setFlightResults(Array.isArray(d) ? d : []);
      appInsights.trackEvent({ 
        name: 'FindFlights', 
        properties: { 
          source: 'PnrGenerator', segForm, paxCount, 
          resultCount: Array.isArray(d) ? d.length : 0 
        } 
      });
    } catch (err) { 
      setFlightResults([]);
      appInsights.trackException({
        exception: err,
        properties: { source: 'PnrGenerator', action: 'findFlights', segForm, paxCount }
      });
    }
    setFindingFlights(false);
  };

  const addSegment = () => {
    if (segForm.FlightNumber) setSegments(p => [...p, { ...segForm }]);
    setSegForm({ DepartureAirport: '', ArrivalAirport: '', FlightNumber: '', DepartureDate: '', Cabin: '' });
    setFlightResults([]);
    setSegDlg(false);
  };

  const addSsr = () => { setSsrList(p => [...p, { ...ssrForm }]); setSsrForm({ SSR_Code: '', SSR_Pax_num: '' }); setSsrDlg(false); };
  const addMvp = () => { setMvpList(p => [...p, { ...mvpForm }]); setMvpForm({ MP_CODE: '', MP_Pax_num: '' }); setMvpDlg(false); };
  const addBag = () => { setBagList(p => [...p, { ...bagForm }]); setBagForm({ Bags_Pax_Num: '', Bag_Count: '' }); setBagDlg(false); };

  /* AI: parse natural-language text and auto-fill form + search flights */
  const handleNlParse = async () => {
    if (!nlText.trim()) return;
    setNlError(''); setNlSuccess(''); setNlLoading(true);
    try {
      const parsed = await pnrApi.parseNaturalLanguage({ text: nlText.trim() });
      if (parsed.error) { setNlError(parsed.error); setNlLoading(false); return; }

      /* Populate form state — backend already searched flights */
      if (parsed.paxCount) setPaxCount(parsed.paxCount);
      if (parsed.aaaCity) setAaaCity(parsed.aaaCity);
      if (typeof parsed.ticket === 'boolean') setTicket(parsed.ticket);
      if (typeof parsed.checkin === 'boolean') setCheckin(parsed.checkin);
      if (parsed.ssrList?.length) setSsrList(parsed.ssrList);
      if (parsed.mvpList?.length) setMvpList(parsed.mvpList);
      if (parsed.bagList?.length) setBagList(parsed.bagList);
      setSegments(parsed.segments || []);

      /* Build success message */
      const parts = [];
      parts.push(`${parsed.paxCount || 1} passenger(s)`);
      parts.push(`${(parsed.segments || []).length} segment(s)`);
      if (parsed.ssrList?.length) parts.push(`${parsed.ssrList.length} SSR(s)`);
      if (parsed.mvpList?.length) parts.push(`${parsed.mvpList.length} MVP tier(s)`);
      if (parsed.bagList?.length) parts.push(`${parsed.bagList.length} bag entry(ies)`);
      const segCount = (parsed.segments || []).length;
      const noFlightSegs = (parsed.segments || []).filter(s => !s.FlightNumber).length;
      let msg = `AI filled: ${parts.join(', ')}.`;
      if (parsed._warnings?.length) msg += '\n' + parsed._warnings.join('\n');
      if (!parsed._warnings?.length && segCount > 0 && !noFlightSegs) msg += ' All flights found.';
      if (noFlightSegs) msg += `\n${noFlightSegs} segment(s) need flight numbers — please add manually.`;
      if (segCount > 0) msg += ' Please review and click Generate PNR.';
      setNlSuccess(msg);

      appInsights.trackEvent({
        name: 'NlParseSuccess',
        properties: {
          source: 'PnrGenerator',
          nlText,
          parsedPaxCount: parsed.paxCount,
          parsedSegments: parsed.segments?.length || 0,
          parsedSsrCount: parsed.ssrList?.length || 0,
          parsedMvpCount: parsed.mvpList?.length || 0,
          parsedBagCount: parsed.bagList?.length || 0,
          warnings: parsed._warnings?.length || 0,
          unparsed: parsed._unparsed?.length || 0,
        }
      });

      if (parsed._unparsed?.length) {
        setNlError(`Could not interpret: ${parsed._unparsed.join('; ')}`);
        appInsights.trackException({
          exception: new Error(`Could not interpret: ${parsed._unparsed.join('; ')}`),
          properties: { source: 'PnrGenerator', action: 'handleNlParse', nlText }
        });
      }
    } catch (e) { 
      setNlError(e.message || 'Failed to parse text with AI'); 
      appInsights.trackException({
        exception: e,
        properties: { source: 'PnrGenerator', action: 'handleNlParse', nlText }
      });
    }
    setNlLoading(false);
  };

  const handleGenerate = async () => {
    if (!paxCount || segments.length === 0) { setError('Passenger count and at least one flight segment are required.'); return; }
    if (checkin) {
      const now = new Date();
      const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const badSeg = segments.find(s => {
        const dep = new Date(s.DepartureDate);
        return dep > in24h;
      });
      if (badSeg) { setError('Check-in requires all departure dates to be within 24 hours from now.'); return; }
    }
    setError(''); setLoading(true); setResult(null);
    try {
      const data = await pnrApi.generatePnr({
        BPC_pax_count: String(paxCount), BPC_ticket: ticket, BPC_Checkin: checkin,
        AAA_City: aaaCity, BPC_Segments: segments,
        BPC_SSR: ssrList,
        BPC_MP: mvpList,
        BPC_BagTags: bagList,
      });
      setResult(data);
    } catch (e) { 
      setError(e.message || 'Failed to generate PNR'); 
      appInsights.trackException({
        exception: e,
        properties: { source: 'PnrGenerator', action: 'handleGenerate', nlText }
      });
    }
    setLoading(false);
  };

  return (
    <Box className="pnr-tab-content" sx={{ position: 'relative', minHeight: 200 }}>
      <AirlineLoader active={loading} text="Generating PNR" />

      {/* ── AI Quick Create ── */}
      <SectionCard title="Quick Create with AI" icon={Sparkles}>
        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mb: 1.5 }}>
          Describe your PNR in plain English — the AI will parse it and fill the form for you.
        </Typography>
        <TextField
          multiline minRows={1} maxRows={1} size="small" fullWidth
          sx={{ ...inputSx, '& .MuiInputBase-inputMultiline': { py: '3px', fontSize: '0.8rem' } }}
          placeholder='e.g. "Create a PNR from SEA to ANC tomorrow, 2 passengers, MVP Gold for pax 1, 1 bag each"'
          value={nlText} onChange={e => setNlText(e.target.value)}
          disabled={nlLoading}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleNlParse(); } }}
        />
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button variant="contained" onClick={handleNlParse}
            disabled={nlLoading || !nlText.trim()}
            startIcon={nlLoading ? null : <Sparkles size={15} />}
            sx={{ ...btnPrimary, bgcolor: '#7c3aed', minWidth: 140,
              '&:hover': { bgcolor: '#6d28d9', boxShadow: '0 4px 14px rgba(124,58,237,0.3)', transform: 'translateY(-1px)' },
              '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' } }}>
            {nlLoading ? 'Parsing…' : 'Parse & Fill'}
          </Button>
          {nlLoading && <AirlineLoaderMini />}
        </Box>
        {nlSuccess && <Fade in><Alert severity="info" sx={{ mt: 1.5, borderRadius: '12px' }}>{nlSuccess}</Alert></Fade>}
        {nlError && <Fade in><Alert severity="warning" sx={{ mt: 1.5, borderRadius: '12px' }}>{nlError}</Alert></Fade>}
      </SectionCard>

      {/* Booking Configuration */}
      <SectionCard title="Booking Configuration" icon={Ticket}>
        <Box className="pnr-field-grid">
          <FormControl fullWidth sx={selectSx}>
            <InputLabel>Passengers *</InputLabel>
            <Select value={paxCount} label="Passengers *" onChange={e => setPaxCount(e.target.value)}>
              {[1, 2, 3, 4, 5, 6, 7].map(n => <MenuItem key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField label="AAA City" value={aaaCity} onChange={e => setAaaCity(e.target.value.toUpperCase())}
            inputProps={{ maxLength: 3 }} fullWidth sx={inputSx} placeholder="e.g. SEA" />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={ticket} onChange={e => setTicket(e.target.checked)}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#0097A9' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0097A9' } }} />}
              label={<Typography sx={{ fontSize: '0.875rem', color: '#475569' }}>Issue Ticket</Typography>} />
            <FormControlLabel
              control={<Switch checked={checkin} onChange={e => setCheckin(e.target.checked)}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#0097A9' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0097A9' } }} />}
              label={<Typography sx={{ fontSize: '0.875rem', color: '#475569' }}>Check-in</Typography>} />
          </Box>
        </Box>
      </SectionCard>

      {/* Flight Segments */}
      <SectionCard title="Flight Segments" icon={Plane}
        action={<Button size="small" startIcon={<Plus size={15} />} onClick={openSegDlg} disabled={!paxCount}
          sx={{ ...btnOutlined, py: 0.6, px: 2, fontSize: '0.8rem' }}>Add Segment</Button>}>
        {segments.length === 0 ? (
          <Box className="pnr-empty-state" sx={{ py: 1.5 }}>
            <Plane size={20} color="#cbd5e1" />
            <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mt: 0.5 }}>No segments added yet</Typography>
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.75rem' }}>Click "Add Segment" to add a flight</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderRadius: '10px', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['From', 'To', 'Flight', 'Date', 'Class', ''].map(h =>
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1.2 }}>{h}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {segments.map((s, i) => (
                  <Fade in key={i} timeout={300}>
                    <TableRow sx={{ '&:hover': { bgcolor: '#fafbfc' }, transition: 'background 0.15s' }}>
                      <TableCell sx={{ fontWeight: 500 }}>{s.DepartureAirport}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{s.ArrivalAirport}</TableCell>
                      <TableCell><Chip label={s.FlightNumber} size="small" sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontWeight: 600, fontSize: '0.75rem' }} /></TableCell>
                      <TableCell sx={{ color: '#64748b', fontSize: '0.8rem' }}>{s.DepartureDate}</TableCell>
                      <TableCell><Chip label={s.Cabin} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} /></TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setSegments(p => p.filter((_, j) => j !== i))}
                          sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: '#ef4444' }, transition: 'all 0.2s' }}>
                          <Trash2 size={14} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      {/* Optional Details */}
      <SectionCard title="Optional Details">
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<Plus size={14} />} onClick={() => setSsrDlg(true)} disabled={!paxCount}
            sx={{ ...btnOutlined, py: 0.5, fontSize: '0.8rem' }}>SSR</Button>
          <Button size="small" startIcon={<Plus size={14} />} onClick={() => setMvpDlg(true)} disabled={!paxCount}
            sx={{ ...btnOutlined, py: 0.5, fontSize: '0.8rem' }}>MVP</Button>
          <Button size="small" startIcon={<Plus size={14} />} onClick={() => setBagDlg(true)} disabled={!paxCount}
            sx={{ ...btnOutlined, py: 0.5, fontSize: '0.8rem' }}>Bags</Button>
          {ssrList.map((s, i) => <Chip key={`s${i}`} label={`${s.SSR_Code} · Pax ${s.SSR_Pax_num}`} size="small" onDelete={() => setSsrList(p => p.filter((_, j) => j !== i))}
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#b45309' } }} />)}
          {mvpList.map((s, i) => <Chip key={`m${i}`} label={`${s.MP_CODE} · Pax ${s.MP_Pax_num}`} size="small" onDelete={() => setMvpList(p => p.filter((_, j) => j !== i))}
            sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#6d28d9' } }} />)}
          {bagList.map((s, i) => <Chip key={`b${i}`} label={`${s.Bag_Count}× Bags · Pax ${s.Bags_Pax_Num}`} size="small" onDelete={() => setBagList(p => p.filter((_, j) => j !== i))}
            sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#047857' } }} />)}
        </Stack>
      </SectionCard>

      {/* Submit */}
      <Button variant="contained" onClick={handleGenerate} disabled={loading || !paxCount || segments.length === 0}
        sx={{ ...btnPrimary, bgcolor: '#01426A', minWidth: 200, py: 1.4, fontSize: '0.9rem',
          '&:hover': { bgcolor: '#002F5F', boxShadow: '0 4px 14px rgba(1,66,106,0.3)', transform: 'translateY(-1px)' },
          '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' } }}>
        {loading ? 'Generating PNR…' : 'Generate PNR'}
      </Button>

      {error && <Fade in><Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>{error}</Alert></Fade>}

      {/* Result */}
      {result && (
        <Grow in timeout={500}>
          <Paper sx={{ mt: 3, p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: result.PNR ? '#ecfdf5' : '#fef2f2',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {result.PNR ? <CheckCircle size={20} color="#059669" /> : <Ticket size={20} color="#dc2626" />}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', letterSpacing: '0.02em', fontFamily: 'monospace' }}>
                  {result.PNR || 'No PNR'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Confirmation Code</Typography>
              </Box>
              {result.PNR && <CopyBtn text={result.PNR} />}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {result.PNR_SUCCESS && <Chip label="PNR Created" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />}
              {result.Ticket_SUCCESS && <Chip label="Ticket Issued" size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }} />}
              {result.CheckIn_SUCCESS && <Chip label="Checked In" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />}
            </Stack>
            {result.Error_Details && <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>{result.Error_Details}</Alert>}
            <ResultTable title="Passenger Information"
              columns={['First Name', 'Last Name', 'Ticket No', 'Ticket Status', 'Seat(s)', 'Loyalty No.', 'Airline Code', 'Tier Status', 'Bag Tag(s)']}
              keys={['FirstName', 'LastName', 'ticketNumber', 'ticketstatus', 'Seats', 'loyaltyNumber', 'airlineCode', 'tierStatus', 'BagTags']}
              rows={result.Pax_Info} />
            <ResultTable title="Segment Information"
              columns={['From', 'Flight', 'To', 'Date', 'Cabin', 'SSRs']}
              keys={['DepartureAirport', 'FlightNumber', 'ArrivalAirport', 'DepartureDate', 'Cabin', 'SSRs']}
              rows={result.PNR_Segments} />
          </Paper>
        </Grow>
      )}

      {/* ── Segment Dialog ── */}
      <Dialog open={segDlg} onClose={() => setSegDlg(false)} maxWidth="sm" fullWidth
        TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}
        slotProps={{ paper: dialogPaperProps.sx ? { sx: dialogPaperProps.sx } : undefined }}>
        <Box sx={{ position: 'relative', minHeight: 320 }}>
          <AirlineLoaderMini active={findingFlights} text="Searching flights" />
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a', pb: 0.5 }}>Add Flight Segment</DialogTitle>
          <DialogContent sx={{ pt: '16px !important' }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2}>
                <TextField label="Departure Airport" value={segForm.DepartureAirport} placeholder="e.g. SEA"
                  onChange={e => setSegForm({ ...segForm, DepartureAirport: e.target.value.toUpperCase() })}
                  onBlur={() => touchSeg('DepartureAirport')}
                  error={segErr('DepartureAirport')}
                  helperText={segErr('DepartureAirport') ? 'Required' : ''}
                  inputProps={{ maxLength: 3 }} fullWidth required sx={{ ...inputSx, ...(segErr('DepartureAirport') ? inputErrorSx : {}) }} />
                <TextField label="Arrival Airport" value={segForm.ArrivalAirport} placeholder="e.g. SFO"
                  onChange={e => setSegForm({ ...segForm, ArrivalAirport: e.target.value.toUpperCase() })}
                  onBlur={() => touchSeg('ArrivalAirport')}
                  error={segErr('ArrivalAirport')}
                  helperText={segErr('ArrivalAirport') ? 'Required' : ''}
                  inputProps={{ maxLength: 3 }} fullWidth required sx={{ ...inputSx, ...(segErr('ArrivalAirport') ? inputErrorSx : {}) }} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField type="date" label="Departure Date" value={segForm.DepartureDate}
                  onChange={e => setSegForm({ ...segForm, DepartureDate: e.target.value })}
                  onBlur={() => touchSeg('DepartureDate')}
                  error={segErr('DepartureDate')}
                  helperText={segErr('DepartureDate') ? 'Required' : ''}
                  InputLabelProps={{ shrink: true }} fullWidth required sx={{ ...inputSx, ...(segErr('DepartureDate') ? inputErrorSx : {}) }} />
                <FormControl fullWidth required error={segErr('Cabin')} sx={{ ...selectSx, ...(segErr('Cabin') ? inputErrorSx : {}) }}>
                  <InputLabel>Class of Service</InputLabel>
                  <Select value={segForm.Cabin} label="Class of Service"
                    onChange={e => setSegForm({ ...segForm, Cabin: e.target.value })}
                    onBlur={() => touchSeg('Cabin')}>
                    <ListSubheader sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#01426A', letterSpacing: '0.06em' }}>BUSINESS</ListSubheader>
                    {BUSINESS.map(c => <MenuItem key={c} value={c} sx={{ pl: 4 }}>{c}</MenuItem>)}
                    <ListSubheader sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#01426A', letterSpacing: '0.06em' }}>PREMIUM</ListSubheader>
                    {PREMIUM.map(c => <MenuItem key={c} value={c} sx={{ pl: 4 }}>{c}</MenuItem>)}
                    <ListSubheader sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#01426A', letterSpacing: '0.06em' }}>MAIN</ListSubheader>
                    {MAIN.map(c => <MenuItem key={c} value={c} sx={{ pl: 4 }}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              <Button variant="outlined" onClick={findFlights} disabled={findingFlights || !segReqMet}
                startIcon={<PlaneTakeoff size={16} />} sx={{ ...btnOutlined, alignSelf: 'flex-start' }}>
                {findingFlights ? 'Searching…' : 'Find Flights'}
              </Button>
              {flightResults.length > 0 ? (
                <FormControl fullWidth sx={selectSx}>
                  <InputLabel>Select Flight</InputLabel>
                  <Select value={segForm.FlightNumber} label="Select Flight" onChange={e => setSegForm({ ...segForm, FlightNumber: e.target.value })}>
                    {flightResults.map((f, i) => (
                      <MenuItem key={i} value={typeof f === 'string' ? f : (f.FlightNumber || f.flightNumber || JSON.stringify(f))}>
                        {typeof f === 'string' ? f : (f.FlightNumber || f.flightNumber || JSON.stringify(f))}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField label="Flight Number" value={segForm.FlightNumber} placeholder="e.g. AS 1234"
                  onChange={e => setSegForm({ ...segForm, FlightNumber: e.target.value })} fullWidth sx={inputSx} />
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => { setSegDlg(false); setFlightResults([]); setSegTouched({}); }} sx={btnOutlined}>Cancel</Button>
            <Button variant="contained" onClick={addSegment} disabled={!segForm.FlightNumber}
              sx={{ ...btnPrimary, bgcolor: '#01426A', '&:hover': { bgcolor: '#002F5F' } }}>Add Segment</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* ── SSR Dialog ── */}
      <Dialog open={ssrDlg} onClose={() => setSsrDlg(false)} maxWidth="xs" fullWidth
        TransitionComponent={Zoom} slotProps={{ paper: dialogPaperProps.sx ? { sx: dialogPaperProps.sx } : undefined }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>Add SSR Detail</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>SSR Code</InputLabel>
              <Select value={ssrForm.SSR_Code} label="SSR Code" onChange={e => setSsrForm({ ...ssrForm, SSR_Code: e.target.value })}>
                {SSR_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>Passenger</InputLabel>
              <Select value={ssrForm.SSR_Pax_num} label="Passenger" onChange={e => setSsrForm({ ...ssrForm, SSR_Pax_num: e.target.value })}>
                {paxArr.map(n => <MenuItem key={n} value={String(n)}>Passenger {n}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setSsrDlg(false)} sx={btnOutlined}>Cancel</Button>
          <Button variant="contained" onClick={addSsr} disabled={!ssrForm.SSR_Code || !ssrForm.SSR_Pax_num}
            sx={{ ...btnPrimary, bgcolor: '#01426A', '&:hover': { bgcolor: '#002F5F' } }}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* ── MVP Dialog ── */}
      <Dialog open={mvpDlg} onClose={() => setMvpDlg(false)} maxWidth="xs" fullWidth
        TransitionComponent={Zoom} slotProps={{ paper: dialogPaperProps.sx ? { sx: dialogPaperProps.sx } : undefined }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>Add MVP Detail</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>MVP Tier</InputLabel>
              <Select value={mvpForm.MP_CODE} label="MVP Tier" onChange={e => setMvpForm({ ...mvpForm, MP_CODE: e.target.value })}>
                {MVP_CODES.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>Passenger</InputLabel>
              <Select value={mvpForm.MP_Pax_num} label="Passenger" onChange={e => setMvpForm({ ...mvpForm, MP_Pax_num: e.target.value })}>
                {paxArr.map(n => <MenuItem key={n} value={String(n)}>Passenger {n}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setMvpDlg(false)} sx={btnOutlined}>Cancel</Button>
          <Button variant="contained" onClick={addMvp} disabled={!mvpForm.MP_CODE || !mvpForm.MP_Pax_num}
            sx={{ ...btnPrimary, bgcolor: '#01426A', '&:hover': { bgcolor: '#002F5F' } }}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bag Dialog ── */}
      <Dialog open={bagDlg} onClose={() => setBagDlg(false)} maxWidth="xs" fullWidth
        TransitionComponent={Zoom} slotProps={{ paper: dialogPaperProps.sx ? { sx: dialogPaperProps.sx } : undefined }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>Add Bag Detail</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>Passenger</InputLabel>
              <Select value={bagForm.Bags_Pax_Num} label="Passenger" onChange={e => setBagForm({ ...bagForm, Bags_Pax_Num: e.target.value })}>
                {paxArr.map(n => <MenuItem key={n} value={String(n)}>Passenger {n}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>Number of Bags</InputLabel>
              <Select value={bagForm.Bag_Count} label="Number of Bags" onChange={e => setBagForm({ ...bagForm, Bag_Count: e.target.value })}>
                {[1, 2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>{n} {n === 1 ? 'Bag' : 'Bags'}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setBagDlg(false)} sx={btnOutlined}>Cancel</Button>
          <Button variant="contained" onClick={addBag} disabled={!bagForm.Bags_Pax_Num || !bagForm.Bag_Count}
            sx={{ ...btnPrimary, bgcolor: '#01426A', '&:hover': { bgcolor: '#002F5F' } }}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ══════════════════════════════════════════════════
   RETRIEVE PNR TAB
   ══════════════════════════════════════════════════ */
function RetrievePnrTab() {
  const [pnr, setPnr] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleRetrieve = async () => {
    if (!pnr.trim()) return;
    setError(''); setLoading(true); setResult(null);
    try { setResult(await pnrApi.retrievePnr({ PNR: pnr.trim().toUpperCase() })); }
    catch (e) { setError(e.message || 'Failed to retrieve PNR'); }
    setLoading(false);
  };

  return (
    <Box className="pnr-tab-content" sx={{ position: 'relative', minHeight: 200 }}>
      <AirlineLoader active={loading} text="Retrieving PNR" />
      <SectionCard title="Lookup" icon={Search}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField label="Confirmation Code (PNR)" value={pnr}
            onChange={e => setPnr(e.target.value.toUpperCase())} sx={{ ...inputSx, minWidth: 280 }}
            inputProps={{ maxLength: 6, style: { letterSpacing: '0.15em', fontWeight: 600, fontFamily: 'monospace' } }}
            placeholder="e.g. ABCDEF"
            onKeyDown={e => e.key === 'Enter' && handleRetrieve()} />
          <Button variant="contained" onClick={handleRetrieve} disabled={loading || !pnr.trim()}
            startIcon={<Search size={18} />}
            sx={{ ...btnPrimary, bgcolor: '#01426A', minWidth: 140, py: 1.4,
              '&:hover': { bgcolor: '#002F5F', boxShadow: '0 4px 14px rgba(1,66,106,0.3)' },
              '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' } }}>
            Retrieve
          </Button>
        </Stack>
      </SectionCard>
      {error && <Fade in><Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert></Fade>}
      {result?.Error_Details && <Fade in><Alert severity="warning" sx={{ borderRadius: '12px' }}>{result.Error_Details}</Alert></Fade>}
      {result && (
        <Grow in timeout={500}>
          <Box>
            <ResultTable title="Passenger Information"
              columns={['First Name', 'Last Name', 'Ticket No.', 'Ticket Status', 'Seat(s)', 'Loyalty No.', 'Airline Code', 'Tier Status']}
              keys={['FirstName', 'LastName', 'ticketNumber', 'ticketstatus', 'Seats', 'loyaltyNumber', 'airlineCode', 'tierStatus']}
              rows={result.Pax_Info} />
            <ResultTable title="Segment Information"
              columns={['From', 'Flight', 'To', 'Date', 'Cabin', 'SSRs']}
              keys={['DepartureAirport', 'FlightNumber', 'ArrivalAirport', 'DepartureDate', 'Cabin', 'SSRs']}
              rows={result.PNR_Segments} />
          </Box>
        </Grow>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════════════
   BAG STATUS TAB
   ══════════════════════════════════════════════════ */
function BagStatusTab() {
  const [bagtag, setBagtag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    if (!bagtag.trim()) return;
    setError(''); setLoading(true); setResult(null);
    try { setResult(await pnrApi.getBagStatus({ bagtagno: bagtag.trim() })); }
    catch (e) { setError(e.message || 'Failed to check bag status'); }
    setLoading(false);
  };

  const statusLabel = result?.Bag_Status === 'A' ? 'Active' : result?.Bag_Status === 'I' ? 'Inactive' : result?.Bag_Status;

  return (
    <Box className="pnr-tab-content" sx={{ position: 'relative', minHeight: 200 }}>
      <AirlineLoader active={loading} text="Checking bag status" />
      <SectionCard title="Bag Lookup" icon={Luggage}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField label="Bag Tag Number" value={bagtag}
            onChange={e => setBagtag(e.target.value)} sx={{ ...inputSx, minWidth: 280 }}
            placeholder="Enter bag tag number"
            onKeyDown={e => e.key === 'Enter' && handleCheck()} />
          <Button variant="contained" onClick={handleCheck} disabled={loading || !bagtag.trim()}
            startIcon={<Search size={18} />}
            sx={{ ...btnPrimary, bgcolor: '#01426A', minWidth: 140, py: 1.4,
              '&:hover': { bgcolor: '#002F5F', boxShadow: '0 4px 14px rgba(1,66,106,0.3)' },
              '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' } }}>
            Check Status
          </Button>
        </Stack>
      </SectionCard>
      {error && <Fade in><Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert></Fade>}
      {result && (
        <Grow in timeout={500}>
          <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', color: '#334155' }}>
                Tag: <strong style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>{result.BAGTAGNO || bagtag}</strong>
              </Typography>
              {statusLabel && <Chip label={statusLabel} size="small"
                sx={{ fontWeight: 600, bgcolor: result.Bag_Status === 'A' ? '#dcfce7' : '#fee2e2', color: result.Bag_Status === 'A' ? '#166534' : '#991b1b' }} />}
            </Stack>
            <ResultTable title="Passenger Information" columns={['First Name', 'Last Name', 'Record Locator']}
              keys={['FirstName', 'LastName', 'RecordLocator']}
              rows={result.Passenger_Info} />
            <ResultTable title="Flight Information" columns={['From', 'Flight', 'To', 'Date']}
              keys={['DepartureAirport', 'FlightNumber', 'ArrivalAirport', 'FlightDate']}
              rows={result.Flight_Details} />
          </Box>
        </Grow>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════════════
   UPGRADE BOOKING TAB
   ══════════════════════════════════════════════════ */
function UpgradeBookingTab() {
  const [availType, setAvailType] = useState('');
  const [paxCount, setPaxCount] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [upgradeClass, setUpgradeClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const classOpts = availType ? (UPGRADE_AVAIL[availType] || []) : [];

  const handleUpgrade = async () => {
    if (!paxCount || !departureDate || !upgradeClass) return;
    setError(''); setLoading(true); setResult(null);
    try { setResult(await pnrApi.upgradeBooking({ BPC_pax_count: Number(paxCount), DepartureDate: departureDate, Upgrade_Class: upgradeClass })); }
    catch (e) { setError(e.message || 'Failed to find upgrade bookings'); }
    setLoading(false);
  };

  return (
    <Box className="pnr-tab-content" sx={{ position: 'relative', minHeight: 200 }}>
      <AirlineLoader active={loading} text="Finding upgrades" />
      <SectionCard title="Upgrade Search" icon={ArrowUpCircle}>
        <Box className="pnr-field-grid">
          <FormControl fullWidth sx={selectSx}>
            <InputLabel>Availability Type *</InputLabel>
            <Select value={availType} label="Availability Type *" onChange={e => { setAvailType(e.target.value); setUpgradeClass(''); }}>
              {Object.keys(UPGRADE_AVAIL).map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={selectSx}>
            <InputLabel>Passengers *</InputLabel>
            <Select value={paxCount} label="Passengers *" onChange={e => setPaxCount(e.target.value)}>
              {[1, 2, 3, 4, 5, 6, 7].map(n => <MenuItem key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField type="date" label="Departure Date" value={departureDate}
            onChange={e => setDepartureDate(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth required sx={inputSx} />
          <FormControl fullWidth sx={selectSx} disabled={!classOpts.length}>
            <InputLabel>Upgrade Class *</InputLabel>
            <Select value={upgradeClass} label="Upgrade Class *" onChange={e => setUpgradeClass(e.target.value)}>
              {classOpts.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Button variant="contained" onClick={handleUpgrade}
          disabled={loading || !paxCount || !departureDate || !upgradeClass}
          startIcon={<ArrowUpCircle size={18} />}
          sx={{ ...btnPrimary, mt: 2.5, bgcolor: '#01426A', minWidth: 180, py: 1.4,
            '&:hover': { bgcolor: '#002F5F', boxShadow: '0 4px 14px rgba(1,66,106,0.3)' },
            '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' } }}>
          {loading ? 'Searching…' : 'Find Upgrades'}
        </Button>
      </SectionCard>
      {error && <Fade in><Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert></Fade>}
      {result && (
        <Grow in timeout={500}>
          <Box>
            <ResultTable title="Upgrade-Eligible Flights"
              columns={['From', 'To', 'Flight', 'Upgradable Seats']}
              keys={['DepartureAirport', 'ArrivalAirport', 'FlightNumber', 'UpgradableSeats']}
              rows={result.upgrade_list} />
          </Box>
        </Grow>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════ */
export const PnrGenerator = () => {
  const [tab, setTab] = useState(0);

  return (
    <div className="pnr-generator-page">
      {/* Hero header */}
      <div className="pnr-page-header">
        <div className="pnr-header-content">
          <div className="pnr-title-row">
            <div className="pnr-title-icon"><Ticket size={28} /></div>
            <div>
              <h1 className="pnr-page-title">PNR Generator</h1>
              <p className="pnr-page-subtitle">Create, retrieve, and manage test PNRs for QA environments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <Paper className="pnr-content-card" elevation={0}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            minHeight: 48, px: 1,
            '& .MuiTab-root': {
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#64748b',
              minHeight: 48, borderRadius: '10px 10px 0 0', transition: 'all 0.2s ease',
              '&:hover': { color: '#334155', bgcolor: '#f8fafc' },
              '&.Mui-selected': { color: '#01426A' },
            },
            '& .MuiTabs-indicator': {
              height: 3, borderRadius: '3px 3px 0 0', bgcolor: '#0097A9',
            },
          }}>
          <Tab icon={<Ticket size={16} />} iconPosition="start" label="Generate PNR" />
          <Tab icon={<Search size={16} />} iconPosition="start" label="Retrieve PNR" />
          <Tab icon={<Luggage size={16} />} iconPosition="start" label="Bag Status" />
          <Tab icon={<ArrowUpCircle size={16} />} iconPosition="start" label="Upgrade Booking" />
        </Tabs>
        <Box className="pnr-tab-divider" />
        <Box sx={{ pt: 3 }}>
          {tab === 0 && <GeneratePnrTab />}
          {tab === 1 && <RetrievePnrTab />}
          {tab === 2 && <BagStatusTab />}
          {tab === 3 && <UpgradeBookingTab />}
        </Box>
      </Paper>
    </div>
  );
};

export default PnrGenerator;
